// ───────────────────────────────────────────────────
// src/features/stock/components/AddModifyProduct.tsx
// ───────────────────────────────────────────────────
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchProductsApi,
  createProductApi,
  updateProductApi,
  deleteProductApi,
  NewProductPayload,
  UpdateProductPayload,
  DeleteProductPayload,
} from '../api/productApi';
import {
  getStockHistoryApi,
  openBalanceApi,
  GetStockHistoryResult,
  OpenBalancePayload,
} from '../../stock/api/stockApi';
import {
  recordStockMovementApi,
  RecordStockMovementPayload,
  RecordStockMovementResult,
} from '../../stock/api/stockApi';    // ← your new API
import ExpandableTable from '../../../../shared/components/ui/ExpandableTable';
import { ColumnDef } from '../../../../shared/components/ui/MinimalisticTable';
import ProductAttributes from './ProductAttributes';
import type { Product } from '../../stock/api/productApi'
import ProductActionCell from './ProductActionCell';

// ─── small hook to fetch latest‐only stock and timestamps ───────────────
function useCurrentStock(productIds: number[]) {
  const { data } = useQuery<GetStockHistoryResult, Error>({
    queryKey: ['productManagement', 'stock', 'current'],
    queryFn: () => getStockHistoryApi({ id_products: productIds, last_only: true }),
    enabled: productIds.length > 0,
    staleTime: 60_000,
  });

  return useMemo(() => {
    const qtyMap: Record<number, number | null> = {};
    const updatedMap: Record<number, string | null> = {};
    data?.history.forEach((r) => {
      const level = (r as any).level ?? r.qty;
      qtyMap[r.product_id] = level;
      updatedMap[r.product_id] = r.timestamp;
    });
    productIds.forEach((id) => {
      if (qtyMap[id] === undefined) {
        qtyMap[id] = null;
        updatedMap[id] = null;
      }
    });
    return { qtyMap, updatedMap };
  }, [data, productIds]);
}

export default function AddModifyProduct() {
  const qc = useQueryClient();

  // 1) products + stock
  const { data: products = [] } = useQuery<Product[], Error>({
    queryKey: ['productManagement', 'products'],
    queryFn: fetchProductsApi,
    staleTime: 60_000,
  });
  const sorted = useMemo(() => [...products].sort((a, b) => a.id_product - b.id_product), [products]);
  const ids = sorted.map((p) => p.id_product);
  const { qtyMap, updatedMap } = useCurrentStock(ids);

  // 2) mutations
  const createM = useMutation({
    mutationFn: (payload: NewProductPayload) => createProductApi(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['productManagement', 'products'] }),
  });
  const updateM = useMutation({
    mutationFn: (payload: UpdateProductPayload) => updateProductApi(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['productManagement', 'products'] }),
  });
  const deleteM = useMutation({
    mutationFn: (payload: DeleteProductPayload) => deleteProductApi(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['productManagement', 'products'] }),
  });
  const openBalM = useMutation({
    mutationFn: (payload: OpenBalancePayload) => openBalanceApi(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['productManagement', 'stock', 'current'] });
      setEditingStockId(null);
    },
  });

  const recordMovementM = useMutation<
    RecordStockMovementResult,
    Error,
    RecordStockMovementPayload
  >({
    mutationFn: (payload) => recordStockMovementApi(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['productManagement', 'stock', 'current'] });
      setEditingStockId(null);
    },
  });

  // 3) inline new‐row + stock‐edit state
  const [adding, setAdding] = useState(false);
  const [vals, setVals] = useState({
    product_sku: '',
    product_name: '',
    brand_name: '',
    product_price_purchase: 0,
    product_price_sales: 0,
  });
  const [editingStockId, setEditingStockId] = useState<number | null>(null);
  const [stockQty, setStockQty] = useState(0);

  // 4) new‐row handlers
  const onNew = () => {
    setAdding(true);
    setVals({
      product_sku: '',
      product_name: '',
      brand_name: '',
      product_price_purchase: 0,
      product_price_sales: 0,
    });
  };
  const onAcceptNew = () => {
    if (!vals.product_sku.trim() || !vals.product_name.trim()) return;
    createM.mutate({ ...vals, unit_of_measure: 'each' });
    setAdding(false);
  };
  const onCancelNew = () => {
    setAdding(false);
    setVals((v) => ({
      ...v,
      product_sku: '',
      product_name: '',
      brand_name: '',
      product_price_purchase: 0,
      product_price_sales: 0,
    }));
  };

  // 5) table columns
  type Row = Product & { __isNew?: boolean };
  const columns: ColumnDef<Row>[] = [
    { header: 'Name', accessor: 'product_name' },
    { header: 'SKU', accessor: 'product_sku' },
    { header: 'Brand', accessor: 'brand_name' },
    { header: 'Purchase Price', accessor: 'product_price_purchase' },
    { header: 'Sale Price', accessor: 'product_price_sales' },
    {
      header: 'Stock',
      accessor: 'id_product',
      Cell: (_v, row) => {
        const pid = row.id_product;
        const qty = qtyMap[pid];

        // ─── OPEN‐BALANCE MODE ────────────────────────
        // only show the textbox here if there is no existing stock
        if (qty == null && editingStockId === pid) {
          return (
            <input
              type="number"
              min={0}
              value={stockQty}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v >= 0) setStockQty(v);
              }}
              onClick={(e) => e.stopPropagation()}
              className="bg-neutral-700 text-neutral-100 px-2 py-1 rounded w-20 appearance-none no-spinner"
            />
          );
        }

        // ─── NEEDS OPEN BALANCE ───────────────────────
        if (qty == null) {
          return (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setEditingStockId(pid);
                setStockQty(0);
              }}
              className="text-blue-500 hover:text-blue-300"
            >
              Open Balance
            </button>
          );
        }

        // ─── NORMAL DISPLAY ────────────────────────────
        return <span>{qty}</span>;
      },
    },
    { header: 'Product Created', accessor: 'timestamp' },
    {
      header: 'Actions',
      accessor: '',
      Cell: (_v, row) => (
        <ProductActionCell
          row={row}
          editingStockId={editingStockId}
          setEditingStockId={setEditingStockId}
          stockQty={stockQty}
          setStockQty={setStockQty}
          currentQty={qtyMap[row.id_product]}
          openBalM={openBalM}
          recordM={recordMovementM}
          updateM={updateM}
          deleteM={deleteM}
        />
      ),
    },
  ];

  // 6) table data (with optional new‐row)
  const data: Row[] = adding
    ? ([{ ...vals, id_product: 0, timestamp: '', __isNew: true }] as Row[]).concat(sorted)
    : sorted;

  return (
    <ExpandableTable<Row>
      columns={columns}
      data={data}
      noDataMessage="No products to modify."
      showNewButton={!adding}
      onNew={onNew}
      editableAccessors={[
        'product_sku',
        'product_name',
        'brand_name',
        'product_price_purchase',
        'product_price_sales',
      ]}
      newRowValues={vals}
      onNewRowChange={(accessor, val) =>
        setVals((prev) => ({
          ...prev,
          [accessor]: accessor.includes('price') ? Number(val) : val,
        }))
      }
      onAcceptNew={onAcceptNew}
      onCancelNew={onCancelNew}
      rowKey={(r) => r.id_product}
      renderSubRow={(r) => <ProductAttributes productId={r.id_product} />}
    />
  );
}
